// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, User, Mail, DollarSign, Percent, MapPin, FileText, Users, CheckCircle, Banknote, Loader2, ExternalLink, Copy } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { format, subMonths, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Territory to membership slug mapping
const TERRITORY_TO_SLUG: Record<string, string> = {
  'Missouri': 'press_mo',
  'Kansas': 'press_ks',
  "Women's": 'womens_press',
  'Wisconsin': 'wisconsin_press',
  'Events': 'event_press',
  'Illinois': 'illinois_press',
  'Iowa': 'iowa_press',
  'Oklahoma': 'oklahoma_press',
};

const SUBSCRIPTION_PRICE_DOLLARS = 9.99;

interface VendorTerritory {
  id: string;
  vendor_profile_id: string;
  state: string | null;
  sport: string | null;
}

interface VendorProfile {
  id: string;
  name: string | null;
  email: string | null;
  venmo_handle: string | null;
  per_article_rate: number | null;
  revenue_percentage: number | null;
  vendor_territories: VendorTerritory[];
}

interface VendorWithCalculations extends VendorProfile {
  articleEarnings: { count: number; rate: number; total: number };
  revenueShare: { subscriberCount: number; percentage: number; totalRevenue: number; share: number };
  totalEarned: number;
  alreadyPaid: number;
  amountOwed: number;
}

const PayVendorsTab: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [vendors, setVendors] = useState<VendorProfile[]>([]);
  const [articleData, setArticleData] = useState<Map<string, number>>(new Map());
  const [subscriberData, setSubscriberData] = useState<Map<string, number>>(new Map());
  const [paymentData, setPaymentData] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  
  // Mark as paid dialog state
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedVendorForPayment, setSelectedVendorForPayment] = useState<VendorWithCalculations | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Generate last 6 months options
  const monthOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const date = subMonths(now, i);
      options.push({
        value: format(date, 'yyyy-MM'),
        label: format(date, 'MMMM yyyy'),
      });
    }
    return options;
  }, []);

  // Set default month to current month
  useEffect(() => {
    if (!selectedMonth && monthOptions.length > 0) {
      setSelectedMonth(monthOptions[0].value);
    }
  }, [monthOptions, selectedMonth]);

  // Fetch vendors with vendor_press role
  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      try {
        // First fetch vendor profiles
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, name, email, venmo_handle, per_article_rate, revenue_percentage')
          .eq('role', 'vendor_press')
          .order('name', { ascending: true });

        if (profilesError) {
          console.error('Error fetching vendors:', profilesError);
          toast.error('Failed to load vendors. Please ensure you are logged in as an admin.');
          return;
        }

        if (!profilesData || profilesData.length === 0) {
          console.log('No vendors found with role vendor_press');
          setVendors([]);
          return;
        }

        console.log('Fetched vendor profiles:', profilesData.length);

        // Then fetch territories separately to avoid RLS join issues
        const vendorIds = profilesData.map(v => v.id);
        const { data: territoriesData, error: territoriesError } = await supabase
          .from('vendor_territories')
          .select('id, vendor_profile_id, state, sport')
          .in('vendor_profile_id', vendorIds);

        if (territoriesError) {
          console.error('Error fetching territories:', territoriesError);
          // Continue without territories rather than failing
        }

        console.log('Fetched territories:', territoriesData?.length || 0, territoriesData);

        // Map territories to vendors
        const territoriesByVendor = new Map<string, VendorTerritory[]>();
        (territoriesData || []).forEach(t => {
          const existing = territoriesByVendor.get(t.vendor_profile_id) || [];
          existing.push(t);
          territoriesByVendor.set(t.vendor_profile_id, existing);
        });

        const vendorsWithTerritories = profilesData.map(v => ({
          ...v,
          vendor_territories: territoriesByVendor.get(v.id) || []
        }));

        console.log('Vendors with territories:', vendorsWithTerritories.map(v => ({
          name: v.name,
          territories: v.vendor_territories.map(t => t.state)
        })));

        setVendors(vendorsWithTerritories);
      } catch (err) {
        console.error('Error:', err);
        toast.error('An unexpected error occurred while loading vendors.');
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, []);

  // Fetch article counts for selected month
  useEffect(() => {
    const fetchArticleCounts = async () => {
      if (!selectedMonth || vendors.length === 0) return;

      setArticlesLoading(true);
      try {
        const monthDate = parseISO(`${selectedMonth}-01`);
        const monthStart = startOfMonth(monthDate);
        const monthEnd = endOfMonth(monthDate);

        // Article counts no longer available (press_articles removed)
        setArticleData(new Map<string, number>());
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setArticlesLoading(false);
      }
    };

    fetchArticleCounts();
  }, [selectedMonth, vendors]);

  // Fetch subscriber counts for each vendor's territories
  useEffect(() => {
    const fetchSubscriberCounts = async () => {
      if (vendors.length === 0) return;

      setSubscribersLoading(true);
      try {
        const allSlugs = new Set<string>();
        vendors.forEach((vendor) => {
          vendor.vendor_territories.forEach((territory) => {
            if (territory.state && TERRITORY_TO_SLUG[territory.state]) {
              allSlugs.add(TERRITORY_TO_SLUG[territory.state]);
            }
          });
        });

        if (allSlugs.size === 0) {
          setSubscriberData(new Map());
          return;
        }

        const { data, error } = await supabase
          .from('profile_memberships')
          .select('membership_slug')
          .eq('is_active', true)
          .eq('price_paid', SUBSCRIPTION_PRICE_DOLLARS)
          .in('membership_slug', Array.from(allSlugs))
          .neq('membership_slug', 'vendor_press');

        if (error) {
          console.error('Error fetching subscribers:', error);
          return;
        }

        const slugCounts = new Map<string, number>();
        (data || []).forEach((membership) => {
          if (membership.membership_slug) {
            slugCounts.set(
              membership.membership_slug,
              (slugCounts.get(membership.membership_slug) || 0) + 1
            );
          }
        });

        const vendorSubscriberMap = new Map<string, number>();
        vendors.forEach((vendor) => {
          let totalSubscribers = 0;
          vendor.vendor_territories.forEach((territory) => {
            if (territory.state && TERRITORY_TO_SLUG[territory.state]) {
              const slug = TERRITORY_TO_SLUG[territory.state];
              totalSubscribers += slugCounts.get(slug) || 0;
            }
          });
          vendorSubscriberMap.set(vendor.id, totalSubscribers);
        });

        setSubscriberData(vendorSubscriberMap);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setSubscribersLoading(false);
      }
    };

    fetchSubscriberCounts();
  }, [vendors]);

  // Fetch payment history for selected month (as a reusable callback)
  const fetchPayments = useCallback(async () => {
    if (!selectedMonth || vendors.length === 0) return;

    setPaymentsLoading(true);
    try {
      const monthDate = parseISO(`${selectedMonth}-01`);
      const monthStart = startOfMonth(monthDate);
      const monthEnd = endOfMonth(monthDate);

      const { data, error } = await supabase
        .from('press_payouts')
        .select('vendor_id, amount_cents')
        .eq('is_paid', true)
        .gte('paid_at', monthStart.toISOString())
        .lte('paid_at', monthEnd.toISOString());

      if (error) {
        console.error('Error fetching payments:', error);
        return;
      }

      // Sum payments per vendor (convert cents to dollars)
      const paymentMap = new Map<string, number>();
      (data || []).forEach((payout) => {
        if (payout.vendor_id) {
          const currentTotal = paymentMap.get(payout.vendor_id) || 0;
          paymentMap.set(payout.vendor_id, currentTotal + (payout.amount_cents || 0) / 100);
        }
      });

      setPaymentData(paymentMap);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setPaymentsLoading(false);
    }
  }, [selectedMonth, vendors]);

  // Fetch payments when month or vendors change
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Handle marking a vendor as paid
  const handleMarkAsPaid = async () => {
    if (!selectedVendorForPayment || selectedVendorForPayment.amountOwed <= 0) return;

    setIsProcessingPayment(true);
    try {
      const amountCents = Math.round(selectedVendorForPayment.amountOwed * 100);
      
      const { error } = await supabase
        .from('press_payouts')
        .insert({
          vendor_id: selectedVendorForPayment.id,
          amount_cents: amountCents,
          is_paid: true,
          paid_at: new Date().toISOString(),
          payout_requested: false,
        });

      if (error) {
        console.error('Error recording payment:', error);
        toast.error('Failed to record payment');
        return;
      }

      // Refresh payment data
      await fetchPayments();

      toast.success(`Payment of $${selectedVendorForPayment.amountOwed.toFixed(2)} to ${selectedVendorForPayment.name || 'vendor'} recorded`);
      setPayDialogOpen(false);
      setSelectedVendorForPayment(null);
    } catch (err) {
      console.error('Error:', err);
      toast.error('An error occurred while recording payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Open payment confirmation dialog
  const openPayDialog = (vendor: VendorWithCalculations) => {
    setSelectedVendorForPayment(vendor);
    setPayDialogOpen(true);
  };

  // Get territories as comma-separated string
  const getTerritoriesDisplay = (territories: VendorTerritory[]) => {
    if (!territories || territories.length === 0) return 'No territories assigned';
    const states = territories
      .map((t) => t.state)
      .filter(Boolean)
      .sort();
    return states.length > 0 ? states.join(', ') : 'No states assigned';
  };

  // Calculate article earnings for a vendor
  const getArticleEarnings = (vendorId: string, perArticleRate: number | null) => {
    const count = articleData.get(vendorId) || 0;
    const rate = perArticleRate ?? 15;
    const total = count * rate;
    return { count, rate, total };
  };

  // Calculate revenue share for a vendor
  const getRevenueShare = (vendorId: string, revenuePercentage: number | null) => {
    const subscriberCount = subscriberData.get(vendorId) || 0;
    const percentage = revenuePercentage ?? 0;
    const totalRevenue = subscriberCount * SUBSCRIPTION_PRICE_DOLLARS;
    const share = totalRevenue * (percentage / 100);
    return { subscriberCount, percentage, totalRevenue, share };
  };

  // Generate Venmo deep link URL
  const getVenmoUrl = (venmoHandle: string, amountOwed: number) => {
    // Remove @ symbol if present
    const cleanHandle = venmoHandle.startsWith('@') ? venmoHandle.slice(1) : venmoHandle;
    
    // Get the selected month/year for the note
    const monthDate = selectedMonth ? parseISO(`${selectedMonth}-01`) : new Date();
    const monthName = format(monthDate, 'MMMM');
    const year = format(monthDate, 'yyyy');
    
    // Build the Venmo URL
    const note = encodeURIComponent(`RecruitLook Press Payout - ${monthName} ${year}`);
    return `https://venmo.com/?txn=pay&audience=private&recipients=${encodeURIComponent(cleanHandle)}&amount=${amountOwed.toFixed(2)}&note=${note}`;
  };

  // Copy email to clipboard
  const copyEmailToClipboard = (email: string) => {
    navigator.clipboard.writeText(email).then(() => {
      toast.success(`Email copied: ${email}`);
    }).catch(() => {
      toast.error('Failed to copy email');
    });
  };

  // Calculate vendors with all data and sort by amount owed
  const vendorsWithCalculations: VendorWithCalculations[] = useMemo(() => {
    return vendors
      .map((vendor) => {
        const articleEarnings = getArticleEarnings(vendor.id, vendor.per_article_rate);
        const revenueShare = getRevenueShare(vendor.id, vendor.revenue_percentage);
        const totalEarned = articleEarnings.total + revenueShare.share;
        const alreadyPaid = paymentData.get(vendor.id) || 0;
        const amountOwed = Math.max(0, totalEarned - alreadyPaid);

        return {
          ...vendor,
          articleEarnings,
          revenueShare,
          totalEarned,
          alreadyPaid,
          amountOwed,
        };
      })
      .sort((a, b) => b.amountOwed - a.amountOwed); // Sort by amount owed descending
  }, [vendors, articleData, subscriberData, paymentData]);

  // Calculate summary totals
  const summaryTotals = useMemo(() => {
    const totalOwed = vendorsWithCalculations.reduce((sum, v) => sum + v.amountOwed, 0);
    const totalPaid = vendorsWithCalculations.reduce((sum, v) => sum + v.alreadyPaid, 0);
    const totalEarned = vendorsWithCalculations.reduce((sum, v) => sum + v.totalEarned, 0);
    const vendorsWithBalance = vendorsWithCalculations.filter((v) => v.amountOwed > 0).length;

    return { totalOwed, totalPaid, totalEarned, vendorsWithBalance, totalVendors: vendors.length };
  }, [vendorsWithCalculations, vendors.length]);

  const isDataLoading = articlesLoading || subscribersLoading || paymentsLoading;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Month Selector */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-muted-foreground">Select Month:</label>
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Banner */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Owed (All Vendors)</p>
              {isDataLoading ? (
                <Skeleton className="h-8 w-24 mx-auto mt-1" />
              ) : (
                <p className="text-2xl font-bold text-destructive font-mono">
                  ${summaryTotals.totalOwed.toFixed(2)}
                </p>
              )}
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Already Paid</p>
              {isDataLoading ? (
                <Skeleton className="h-8 w-24 mx-auto mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary font-mono">
                  ${summaryTotals.totalPaid.toFixed(2)}
                </p>
              )}
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Earned</p>
              {isDataLoading ? (
                <Skeleton className="h-8 w-24 mx-auto mt-1" />
              ) : (
                <p className="text-2xl font-bold text-primary font-mono">
                  ${summaryTotals.totalEarned.toFixed(2)}
                </p>
              )}
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Vendors</p>
              <p className="text-2xl font-bold text-foreground">
                {summaryTotals.vendorsWithBalance} <span className="text-sm font-normal text-muted-foreground">/ {summaryTotals.totalVendors} owed</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vendor Count */}
      <p className="text-sm text-muted-foreground">
        Showing {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} (sorted by amount owed)
      </p>

      {/* Vendor Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {vendorsWithCalculations.map((vendor) => {
          const isPaidInFull = vendor.amountOwed === 0;
          
          return (
            <Card 
              key={vendor.id} 
              className={cn(
                "border border-border transition-all",
                isPaidInFull && "opacity-60 bg-muted/30"
              )}
            >
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  {isPaidInFull ? (
                    <CheckCircle className="h-5 w-5 text-secondary" />
                  ) : (
                    <User className="h-5 w-5 text-primary" />
                  )}
                  {vendor.name || 'Unknown Vendor'}
                  {isPaidInFull && (
                    <Badge variant="outline" className="ml-auto text-secondary border-secondary">
                      Paid in Full
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Email */}
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Email:</span>
                  <span>{vendor.email || 'No email'}</span>
                </div>

                {/* Venmo Handle */}
                <div className="flex items-center gap-2 text-sm">
                  {vendor.venmo_handle ? (
                    <>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Venmo:</span>
                      <Badge variant="secondary">{vendor.venmo_handle}</Badge>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      <span className="text-destructive font-medium">⚠️ No Venmo on file</span>
                    </>
                  )}
                </div>

                {/* Territories */}
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span className="text-muted-foreground">Territories:</span>
                  <span className="flex-1">{getTerritoriesDisplay(vendor.vendor_territories)}</span>
                </div>

                {/* Per-Article Rate */}
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Per-Article Rate:</span>
                  <Badge variant="outline" className="font-mono">
                    ${vendor.per_article_rate ?? 15}
                  </Badge>
                </div>

                {/* Revenue Share Percentage */}
                <div className="flex items-center gap-2 text-sm">
                  <Percent className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Revenue Share:</span>
                  <Badge variant="outline" className="font-mono">
                    {vendor.revenue_percentage ?? 0}%
                  </Badge>
                </div>

                {/* Article Earnings Section */}
                <div className="mt-4 pt-3 border-t border-border">
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="font-medium text-foreground">Article Earnings</span>
                    {articlesLoading && <Skeleton className="h-4 w-20" />}
                  </div>
                  
                  {!articlesLoading && (
                    <div className="pl-6 space-y-1">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Articles this month:</span>{' '}
                        <span className="font-medium">{vendor.articleEarnings.count}</span>
                      </div>
                      <div className="text-sm">
                        {vendor.articleEarnings.count > 0 ? (
                          <>
                            <span className="text-muted-foreground">Article earnings:</span>{' '}
                            <span className="font-mono">
                              {vendor.articleEarnings.count} × ${vendor.articleEarnings.rate} ={' '}
                              <span className="text-primary font-semibold">${vendor.articleEarnings.total.toFixed(2)}</span>
                            </span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">0 articles — $0.00</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Subscription Revenue Share Section */}
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <Users className="h-4 w-4 text-primary" />
                    <span className="font-medium text-foreground">Subscription Revenue Share</span>
                    {subscribersLoading && <Skeleton className="h-4 w-20" />}
                  </div>
                  
                  {!subscribersLoading && (
                    <div className="pl-6 space-y-1">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Subscribers in territory:</span>{' '}
                        <span className="font-medium">{vendor.revenueShare.subscriberCount}</span>
                      </div>
                      <div className="text-sm">
                        {vendor.revenueShare.subscriberCount > 0 && vendor.revenueShare.percentage > 0 ? (
                          <>
                            <span className="text-muted-foreground">Revenue share:</span>{' '}
                            <span className="font-mono">
                              {vendor.revenueShare.percentage}% × ${vendor.revenueShare.totalRevenue.toFixed(2)} ={' '}
                              <span className="text-primary font-semibold">${vendor.revenueShare.share.toFixed(2)}</span>
                            </span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">
                            {vendor.revenueShare.subscriberCount === 0 
                              ? '0 subscribers — $0.00' 
                              : `0% share — $0.00`}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Summary Section */}
                <div className="pt-3 border-t-2 border-primary/30 bg-muted/30 -mx-6 px-6 py-3 -mb-6 rounded-b-lg space-y-2">
                  {isDataLoading ? (
                    <Skeleton className="h-20 w-full" />
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Total Earned:</span>
                        <span className="font-mono font-medium">${vendor.totalEarned.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Already Paid:</span>
                        <span className="font-mono font-medium text-secondary">
                          -${vendor.alreadyPaid.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <span className="font-semibold text-foreground flex items-center gap-2">
                          <Banknote className="h-4 w-4" />
                          AMOUNT OWED:
                        </span>
                        <span className={cn(
                          "text-xl font-bold font-mono",
                          vendor.amountOwed > 0 ? "text-destructive" : "text-secondary"
                        )}>
                          ${vendor.amountOwed.toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Payment Action Buttons */}
                      <div className="pt-3 space-y-2">
                        {/* Venmo / Email Row */}
                        {vendor.amountOwed > 0 && (
                          <div className="flex gap-2">
                            {vendor.venmo_handle ? (
                              <Button
                                onClick={() => window.open(getVenmoUrl(vendor.venmo_handle!, vendor.amountOwed), '_blank')}
                                variant="secondary"
                                className="flex-1"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                💸 Pay via Venmo
                              </Button>
                            ) : vendor.email ? (
                              <Button
                                onClick={() => copyEmailToClipboard(vendor.email!)}
                                variant="outline"
                                className="flex-1"
                              >
                                <Copy className="h-4 w-4 mr-2" />
                                Copy Email ({vendor.email})
                              </Button>
                            ) : null}
                          </div>
                        )}
                        
                        {/* Mark as Paid Button */}
                        <Button
                          onClick={() => openPayDialog(vendor)}
                          disabled={vendor.amountOwed <= 0}
                          className="w-full"
                          variant={vendor.amountOwed > 0 ? "default" : "outline"}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {vendor.amountOwed > 0 
                            ? `✅ Mark $${vendor.amountOwed.toFixed(2)} as Paid` 
                            : 'Fully Paid'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {vendors.length === 0 && (
          <div className="col-span-2 text-center py-12 space-y-2">
            <p className="text-muted-foreground">No vendors found with role "vendor_press"</p>
            <p className="text-sm text-muted-foreground">
              Please ensure you are logged in as an admin to view vendor data.
            </p>
          </div>
        )}
      </div>

      {/* Payment Confirmation Dialog */}
      <AlertDialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Mark <span className="font-semibold text-foreground">${selectedVendorForPayment?.amountOwed.toFixed(2)}</span> as paid to{' '}
              <span className="font-semibold text-foreground">{selectedVendorForPayment?.name || 'this vendor'}</span>?
              <br /><br />
              This will record a payment in the system and update the vendor's balance to $0.00.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessingPayment}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMarkAsPaid}
              disabled={isProcessingPayment}
              className="bg-primary"
            >
              {isProcessingPayment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Payment
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PayVendorsTab;
